import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getGroups, createGroup, logout, updateDisplayName, getMyTasks, getAllMyGroupsTasks, subscribeToWebPush } from '../services/api';
import '../styles/Layout.css';
import NotificationPopup from '../components/NotificationPopup';

function Layout({ user, setUser, children }) {
  const [groups, setGroups] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#667eea');
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [myTasksCount, setMyTasksCount] = useState(0);
  const [allOpenCount, setAllOpenCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const colors = [
    '#667eea', '#764ba2', '#f093fb', '#4facfe',
    '#43e97b', '#fa709a', '#fee140', '#30cfd0',
    '#a8edea', '#ff6b6b', '#4ecdc4', '#45b7d1'
  ];

  useEffect(() => {
    loadGroups();
    loadMyTasksCount();
    
    // הרשמה להתראות Web Push
    const initWebPush = async () => {
      const result = await subscribeToWebPush();
      if (result.success) {
        console.log('✅ Web Push enabled');
      } else {
        console.log('Web Push not enabled:', result.reason);
      }
    };
    initWebPush();
  }, []);

  // Reload groups when location changes (e.g., after creating group)
  useEffect(() => {
    loadGroups();
    loadMyTasksCount();
  }, [location.pathname]);

  // Close mobile menu when navigating
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Listen to socket events for real-time updates
  useEffect(() => {
    const handleTaskUpdate = () => {
      loadMyTasksCount();
    };

    window.addEventListener('task-updated', handleTaskUpdate);
    window.addEventListener('task-created', handleTaskUpdate);
    window.addEventListener('task-deleted', handleTaskUpdate);

    return () => {
      window.removeEventListener('task-updated', handleTaskUpdate);
      window.removeEventListener('task-created', handleTaskUpdate);
      window.removeEventListener('task-deleted', handleTaskUpdate);
    };
  }, []);

  const loadGroups = async () => {
    try {
      const response = await getGroups();
      setGroups(response.data.groups);
    } catch (error) {
      console.error('שגיאה בטעינת קבוצות:', error);
    }
  };

  const loadMyTasksCount = async () => {
    try {
      const response = await getMyTasks();
      setMyTasksCount(response.data.tasks.length);
      
      const allResponse = await getAllMyGroupsTasks();
      const allTasks = allResponse.data.tasks;
      setAllOpenCount(allTasks.length);
      setInProgressCount(allTasks.filter(t => t.status === 'in-progress').length);
    } catch (error) {
      console.error('שגיאה בטעינת משימות:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const response = await createGroup({ 
        name: newGroupName,
        color: newGroupColor 
      });
      setNewGroupName('');
      setNewGroupColor('#667eea');
      setShowCreateModal(false);
      loadGroups();
      // Navigate to the new group
      if (response.data.group) {
        navigate(`/group/${response.data.group._id}`);
      }
    } catch (error) {
      console.error('שגיאה ביצירת קבוצה:', error);
      alert('שגיאה ביצירת קבוצה');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('שגיאה בהתנתקות:', error);
    }
  };

  const handleUpdateDisplayName = async () => {
    if (!newDisplayName.trim()) {
      alert('יש להזין שם תצוגה');
      return;
    }
    try {
      const response = await updateDisplayName(newDisplayName);
      setUser(response.data.user);
      setShowEditNameModal(false);
      setNewDisplayName('');
      alert('שם התצוגה עודכן בהצלחה!');
    } catch (error) {
      console.error('שגיאה בעדכון שם:', error);
      alert(error.response?.data?.message || 'שגיאה בעדכון שם התצוגה');
    }
  };

  const openEditNameModal = () => {
    setNewDisplayName(user.displayName || user.name);
    setShowEditNameModal(true);
  };

  const isActiveGroup = (groupId) => {
    return location.pathname === `/group/${groupId}`;
  };

  const isDashboard = location.pathname === '/dashboard';

  return (
    <div className="app-container">
      <NotificationPopup />
      
      {/* Mobile Header */}
      <header className="mobile-header">
        <button className="hamburger-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <div className="mobile-logo">
          <span>📋</span>
          <span>TaskFlow</span>
        </div>
        <div className="mobile-header-spacer"></div>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      {/* Sidebar - קבוע */}
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-icon">📋</span>
            <h1>TaskFlow</h1>
          </div>
        </div>

        {/* User Info */}
        <div className="sidebar-user">
          <img src={user.picture} alt={user.displayName || user.name} />
          <div className="sidebar-user-info">
            <h4 className="user-name-clickable" onClick={openEditNameModal} title="לחץ לשינוי שם תצוגה">
              {user.displayName || user.name} ✏️
            </h4>
            <p>{user.email}</p>
          </div>
          <button className="btn-logout" onClick={handleLogout} title="התנתק">
            🚪
          </button>
        </div>

        {/* Menu */}
        <nav className="sidebar-menu">
          {/* Dashboard Link */}
          <button 
            className={`menu-item ${isDashboard ? 'active' : ''}`}
            onClick={() => navigate('/dashboard')}
          >
            <span className="icon">🏠</span>
            <span className="label">דף הבית</span>
          </button>

          {/* My Tasks Link */}
          <button 
            className={`menu-item ${location.pathname === '/my-tasks' ? 'active' : ''}`}
            onClick={() => navigate('/my-tasks')}
          >
            <span className="icon">📌</span>
            <span className="label">המשימות שלי</span>
            {myTasksCount > 0 && <span className="tasks-count">{myTasksCount}</span>}
          </button>

          {/* All Open Tasks Link */}
          <button 
            className={`menu-item ${location.pathname === '/all-tasks/open' ? 'active' : ''}`}
            onClick={() => navigate('/all-tasks/open')}
          >
            <span className="icon">📋</span>
            <span className="label">כל הפתוחות</span>
            {allOpenCount > 0 && <span className="tasks-count tasks-count-blue">{allOpenCount}</span>}
          </button>

          {/* In Progress Tasks Link */}
          <button 
            className={`menu-item ${location.pathname === '/all-tasks/in-progress' ? 'active' : ''}`}
            onClick={() => navigate('/all-tasks/in-progress')}
          >
            <span className="icon">🔧</span>
            <span className="label">בטיפול</span>
            {inProgressCount > 0 && <span className="tasks-count tasks-count-orange">{inProgressCount}</span>}
          </button>


{/* Tech Chat Link */}
          <button 
            className={`menu-item ${location.pathname === '/tech-chat' ? 'active' : ''}`}
            onClick={() => navigate('/tech-chat')}
          >
            <span className="icon">🤖</span>
            <span className="label">עזרה טכנית</span>
          </button>

          <div className="menu-section-title">הקבוצות שלי</div>
          
          <div className="groups-menu">
            {groups.map(group => (
              <button 
                key={group._id} 
                className={`group-menu-item ${isActiveGroup(group._id) ? 'active' : ''}`}
                onClick={() => navigate(`/group/${group._id}`)}
              >
                <span className="group-color-dot" style={{ background: group.color || '#667eea' }}></span>
                <span className="label">{group.name}</span>
                <span className="count">{group.members.length} 👥</span>
              </button>
            ))}
          </div>

          <button className="btn-add-group" onClick={() => setShowCreateModal(true)}>
            <span className="icon">+</span>
            <span>קבוצה חדשה</span>
          </button>
        </nav>
      </aside>

      {/* Main Content - משתנה */}
      <main className="main-content">
        {children}
      </main>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setNewGroupName(''); setNewGroupColor('#667eea'); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>קבוצה חדשה</h3>
            
            <div className="form-group">
              <label>שם הקבוצה</label>
              <input
                type="text"
                placeholder="לדוגמה: צוות פיתוח"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="input-field"
                autoFocus
              />
            </div>
            
            <div className="color-picker-section">
              <label>בחר צבע</label>
              <div className="color-picker">
                {colors.map(color => (
                  <div
                    key={color}
                    className={`color-option ${newGroupColor === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewGroupColor(color)}
                  />
                ))}
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleCreateGroup}>צור קבוצה</button>
              <button className="btn-secondary" onClick={() => { setShowCreateModal(false); setNewGroupName(''); setNewGroupColor('#667eea'); }}>ביטול</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Display Name Modal */}
      {showEditNameModal && (
        <div className="modal-overlay" onClick={() => { setShowEditNameModal(false); setNewDisplayName(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>✏️ שינוי שם תצוגה</h3>
            
            <div className="form-group">
              <label>שם התצוגה שלך</label>
              <input
                type="text"
                placeholder="הכנס את השם שלך"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                className="input-field"
                autoFocus
              />
              <small className="form-hint">השם הזה יוצג לכל המשתמשים במערכת</small>
            </div>
            
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleUpdateDisplayName}>שמור</button>
              <button className="btn-secondary" onClick={() => { setShowEditNameModal(false); setNewDisplayName(''); }}>ביטול</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Layout;